import Text from "@/components/Home/v2/shared/Text";
import classNames from "classnames";
import AiEditorCard from "./AiEditorCard";
import SocialConnectCard from "./SocialConnectCard";

export type AlternatingProductsItem = {
  image: string;
  imageAlt?: string;
  /** Optional custom slot — renders a built-in component instead of the image. */
  customSlot?: "social-connect" | "ai-editor";
  eyebrow: string;
  title: string;
  description: string;
  features: string[];
  ctaText?: string;
};

export type AlternatingProductsSectionData = {
  items: [AlternatingProductsItem, AlternatingProductsItem, AlternatingProductsItem];
  finalImage?: string;
  finalImageAlt?: string;
};

const AlternatingProductsSection = ({
  items,
}: AlternatingProductsSectionData) => {
  return (
    <section
      className="w-full"
      style={{
        background: "linear-gradient(180deg, #FFFFFF 0%, #F3F0FF 60%, #E8E4FD 100%)",
      }}
    >
      <div className="container mx-auto px-4 sm:px-6 md:px-8 lg:px-12 py-10 md:py-20 lg:py-24">
        <div className="flex flex-col gap-16 md:gap-24">
          {items.map((item, idx) => {
            const isImageLeft = idx % 2 === 0;

            return (
              <div
                key={idx}
                className="mx-auto grid w-full max-w-6xl grid-cols-1 gap-8 md:grid-cols-2 md:items-center md:gap-16"
              >
                {/* Image / custom card slot */}
                <div
                  className={classNames(
                    "relative aspect-[4/3] w-full overflow-hidden rounded-2xl bg-[#F3F4F6]",
                    isImageLeft ? "md:order-1" : "md:order-2"
                  )}
                  style={{ border: "1px solid #E5E7EB" }}
                >
                  {item.customSlot === "social-connect" ? (
                    <SocialConnectCard />
                  ) : item.customSlot === "ai-editor" ? (
                    <AiEditorCard />
                  ) : (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={item.image}
                      alt={item.imageAlt ?? ""}
                      className="absolute inset-0 h-full w-full object-contain p-6 md:p-8"
                    />
                  )}
                </div>

                {/* Text block */}
                <div
                  className={classNames(
                    "flex flex-col gap-5",
                    isImageLeft ? "md:order-2" : "md:order-1"
                  )}
                >
                  <div className="flex items-center gap-2">
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#007F8C]" />
                    <Text
                      as="span"
                      variant="eyebrow"
                      className="text-[#007F8C]"
                      style={{ letterSpacing: "0.6px" }}
                    >
                      {item.eyebrow}
                    </Text>
                  </div>

                  <Text as="h2" variant="title-lg" className="text-neutralBlack font-semibold -mt-1">
                    {item.title}
                  </Text>

                  <Text as="p" variant="body-lg" className="text-darkSlate04">
                    {item.description}
                  </Text>

                  <ul className="flex flex-col gap-3">
                    {item.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className="mt-0.5 flex-shrink-0 flex items-center justify-center w-5 h-5 rounded-full bg-[#22C55E]">
                          <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </span>
                        <Text as="p" variant="body-md" className="text-darkSlate03">
                          {feature}
                        </Text>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-1">
                    <a
                      href="#"
                      className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#4E5DE0] to-[#5868F7] px-7 py-3 font-inter text-[15px] font-semibold text-white shadow-sm transition-transform hover:scale-[1.02]"
                    >
                      {item.ctaText || "Learn more"}
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default AlternatingProductsSection;
